import { useLocation, useNavigate, useParams } from "react-router-dom";
import "./result.page.scss";
import { useEffect, useState } from "react";
import RESULT_CHECK_IMG from "../assets/images/result-check.png";
import { JobPosition, JobQuestionModel } from "../models/questions.model";
import { getReportSystem, getRoom } from "../services/interview-api.services";
import ErrorModal from "../modals/error-message.modal";
import { useLanguage } from "../providers/language.provider";
import config from "../config";
import { Button, CircularProgress } from "@mui/material";
import { ReportRecommendModel } from "../models/report-recommend.model";
import IMG_JOBHACK from "../assets/images/logo-jobhack.png";
import IMG_COVER from "../assets/images/fully-report-cover.png";
import LOGO_SO_BLACK from "../assets/images/logo-so-black.png";
import LOGO_PROFI from "../assets/images/logo-profi.svg";
import ENGLISH_INTERVIEW_LOGO from "../assets/images/english-interview-logo.png";
import PaymentModal from "../modals/payment.modal";
import { GAConfig } from "../utils/ga-config.util";
import UtilityService from "../utils/ga4.util";
import { trackEvent } from "../utils/trackGA4.util";
import { getPublicInterviewBranding } from "../utils/public-branding.util";

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};
const ResultPage = () => {
  const GAService = UtilityService();
  const { id, candidate_id, email, report_id, report_code } = useParams<{
    id: string;
    candidate_id: string;
    email: string;
    report_id: string;
    report_code: string;
  }>();
  const [roomModel, setRoomModel] = useState<JobQuestionModel>();
  const [isError, setIsError] = useState(false);
  const [isShowPayment, setIsShowPayment] = useState(false);
  const [isRecommending, setIsRecommending] = useState(false);
  const query = useQuery();
  const location = useLocation();
  const [recommend, setRecommend] = useState<ReportRecommendModel>();
  const [reportId, setReportID] = useState("");
  const [isRecommendError, setIsRecommendError] = useState(false);
  const [appId, setAppId] = useState<string | null>("SCOUTOUT");
  const [companyLogoFailed, setCompanyLogoFailed] = useState(false);
  const navigate = useNavigate();
  const { translations, language, switchLanguage } = useLanguage();

  // Check if current ID should be locked to English
  const isEnglishLocked = id && config.language.english_locked_ids.includes(id);

  // Set language to English for whitelisted IDs and prevent changes
  useEffect(() => {
    if (isEnglishLocked) {
      console.log(`Setting English as locked language for ID: ${id}`);
      switchLanguage("en");
    }
  }, [id, isEnglishLocked, switchLanguage]);
  useEffect(() => {
    if (query.get("app_id")) {
      const id = query.get("app_id");
      setAppId(id);
    } else {
      setAppId("SCOUTOUT");
    }
  }, [query]);

  useEffect(() => {
    if (query && report_id && !report_code) {
      const app = query.get("app_id");
      if (app === "INTERVIEW_AI" || app === "PROFI") {
        getReportRecommend(report_id);
        setReportID(report_id);
      }
    } else if (query && report_id && report_code) {
      if (query.get("app_id") === "ENGLISH_INTERVIEW_AI") {
        setReportID(report_code);
      }
    }
  }, [query, report_id, report_code, recommend]);

  useEffect(() => { 
    try {
      if (appId === "ENGLISH_INTERVIEW_AI") {
        return;
      } else {
        if (id && candidate_id && email && !report_code) {
          getRoomDetail(id);
        } else {
          setIsError(true);
          if (appId === "PROFI") {
            window.open(config.partner.profi, "_self");
          } else {
            navigate("/");
          }
        }
      }
    } catch {
      setIsError(true);
    }
  }, [id, candidate_id, email, !report_code]);

  useEffect(() => {
    //  https://english-interview-test.web.app/result?report_code={report_short_id}
    const handlePopState = () => {
      // PROFI อยู่หน้าผลลัพธ์เต็ม (มีปุ่มดูรายงาน) ไม่ redirect ให้กดดูรายงานได้
      if (appId === "PROFI" && recommend) {
        window.history.pushState(null, "", window.location.href);
        return;
      }
      if (appId === "PROFI") {
        trackEvent(GAConfig.action.completeProfile, UtilityService());
        notifyParentToRedirect(appId);
      } else if (appId === "ENGLISH_INTERVIEW_AI") {
        trackEvent(GAConfig.action.completeProfile, UtilityService());
        notifyParentToRedirect(appId);
      } else {
        window.history.pushState(null, "", window.location.href);
        navigate("/");
      }
    };

    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [appId, recommend]);

  const getReportRecommend = (reportId) => {
    setIsRecommending(true);
    try {
      getReportSystem(reportId)
        .then((res) => {
          if (res) {
            console.log(res);
            setRecommend(res);

            setIsRecommending(false);
            setIsRecommendError(false);
          }
        })
        .catch((error) => {
          console.log(error);
          setIsRecommending(false);
          setIsRecommendError(true);
        });
    } catch (e) {
      setIsRecommending(false);
      setIsRecommendError(true);
    }
  };
  const notifyParentToRedirect = (appId: string) => {
    const currentReportId = report_id || reportId;
    const url =
      appId === "PROFI"
        ? (config.partner.profi ?? "")
        : appId === "ENGLISH_INTERVIEW_AI"
          ? (config.partner.english_interview_ai ?? "") + "?report_code=" + (report_code ?? "")
          : (config.partner.profi ?? "");
    const payload: { url: string; reportId?: string } = { url };
    if (appId === "PROFI" && currentReportId) {
      payload.reportId = currentReportId;
    }
    window.parent.postMessage({ type: "REDIRECT", payload }, "*");
  };
  const getRoomDetail = (id) => {
    getRoom(id, email).then((res) => {
      if (res.is_success) {
        const result = res.message as JobQuestionModel;
        console.log(result);
        setRoomModel(result);

        window.parent.postMessage(
          {
            type: "COMPLETED",
            payload: {
              url: config.partner.profi, // or a relative path
            },
          },
          "*"
        );
      }
    });
  };

  const backHome = () => {
    navigate("/");
    trackEvent(GAConfig.action.homePage, GAService);
  };

  const showPayment = () => {
    setIsShowPayment(!isShowPayment);
    trackEvent(GAConfig.action.payment, GAService);
  };

  return (
    <>
      {!recommend && (
        <div className="result-control">
          {/* Header */}
          <div className="result-header">
            {appId === "SCOUTOUT" ? (
              <img src={LOGO_SO_BLACK} className="result-header-logo" alt="ScoutOut" />
            ) : appId === "PROFI" ? (
              <img src={LOGO_PROFI} className="result-header-logo" alt="PROFI" />
            ) : appId === "INTERVIEW_AI" ? (
              <img src={IMG_JOBHACK} className="result-header-logo" alt="Jobhack" />
            ) : appId === "PUBLIC_INTERVIEW_AI" ? (() => {
              // Same source priority as create-public.page / interview-room.page:
              //   1. sessionStorage stash from create-public (most accurate, what the
              //      candidate originally saw)
              //   2. roomModel.job_position.company_logo / logoImageUrl from getRoom
              //   3. IMG_JOBHACK fallback
              const stash = getPublicInterviewBranding(roomModel?.company_id, id);
              const apiLogo = String(
                roomModel?.job_position?.company_logo ||
                (roomModel?.job_position as any)?.logoImageUrl ||
                ""
              ).trim();
              const publicLogoUrl = String(stash?.logoUrl || apiLogo || "").trim();
              const showCompanyLogo = Boolean(publicLogoUrl) && !companyLogoFailed;
              const altCompany =
                stash?.companyName ||
                roomModel?.job_position?.company_name ||
                "Company";
              return (
                <img
                  src={showCompanyLogo ? publicLogoUrl : IMG_JOBHACK}
                  className="result-header-logo"
                  alt={`${altCompany} logo`}
                  onError={() => setCompanyLogoFailed(true)}
                />
              );
            })() : appId === "ENGLISH_INTERVIEW_AI" ? (
              <img src={ENGLISH_INTERVIEW_LOGO} className="result-header-logo" alt="English Interview" />
            ) : null}
          </div>

          {roomModel && (
            <div className="report-control">
              {appId == "HR9" ? (
                <>
                  <img
                    src={RESULT_CHECK_IMG}
                    className="result-check-img"
                  ></img>
                  <span className="text-title-bold">
                    {translations.result.title}
                  </span>
                  <span className="text-sub-title">
                    ขอบคุณที่สัมภาษณ์
                  </span>
                  <span className="text-sub-title">
                    กรุณารอผลภายใน 7-14 วันทำการ
                  </span>
                  {roomModel.job_position && (
                    <>
                      {roomModel.job_position.job_title && (
                        <span className="text-position-name">
                          {roomModel.job_position.job_title}
                        </span>
                      )}
                      {(roomModel.job_position.company_logo || roomModel.job_position.company_name) && (
                        <div className="company-control">
                          {roomModel.job_position.company_logo && (
                            <img
                              src={roomModel.job_position.company_logo}
                              className="company-logo"
                            ></img>
                          )}
                          {roomModel.job_position.company_name && (
                            <span className="company-name">
                              {roomModel.job_position.company_name}
                            </span>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </>
              ) : appId == "SCOUTOUT" ? (
                <>
                  <img
                    src={RESULT_CHECK_IMG}
                    className="result-check-img"
                  ></img>
                  <span className="text-title-bold">
                    {translations.result.title}
                  </span>
                  <span className="text-sub-title">
                    {translations.result.desc_1}
                  </span>
                  <span className="text-position-name">
                    {roomModel.job_position.job_title}
                  </span>
                  <div className="company-control">
                    <img
                      src={roomModel.job_position.company_logo}
                      className="company-logo"
                    ></img>
                    <span className="company-name">
                      {roomModel.job_position.company_name}
                    </span>
                  </div>
                  <span className="text-bottom-gray">
                    {" "}
                    {translations.result.desc_2}
                    {translations.result.desc_3}
                    {translations.result.desc_4}
                  </span>
                  <span className="text-bottom-gray">
                    {translations.result.desc_5}
                    <br></br>
                    {translations.result.desc_6}{" "}
                    {roomModel.job_position.company_name}
                  </span>
                </>
              ) : appId == "PROFI" ? (
                <>
                  {isRecommending && (
                    <>
                      <CircularProgress className="result-check-img"></CircularProgress>
                      <span className="text-title-bold">
                        {translations.result.title}
                      </span>
                      <span className="text-sub-title">
                        กำลังโหลดคำตอบ และ คำแนะนำเพิ่มเติมให้แก่คุณ
                      </span>
                      <span className="text-sub-title">
                        กรุณาอย่าปิดหน้านี้จนกว่าผลลัพธ์จะแสดง
                      </span>
                      {isRecommendError && (
                        <>
                          <span className="text-bottom-gray">
                            กรณีที่รอนานเกินไป กรุณากด โหลดซ้ำ เพื่อเช็คอีกครั้ง
                          </span>
                          <Button
                            className="button-std button-home"
                            onClick={() => getReportRecommend(reportId)}
                          >
                            โหลดซ้ำ
                          </Button>
                        </>
                      )}
                    </>
                  )}
                  {!isRecommending && (
                    <>
                      <img
                        src={RESULT_CHECK_IMG}
                        className="result-check-img"
                      ></img>
                      <span className="text-title-bold">
                        {translations.result_profi.title}
                      </span>
                      <div className="company-control">
                        <img
                          src={roomModel.job_position.company_logo}
                          className="company-logo"
                        ></img>
                        <span className="company-name">
                          {roomModel.job_position.company_name}
                        </span>
                      </div>
                      <span className="text-bottom-gray">
                        {translations.result_profi.desc_1}
                      </span>
                      <span className="text-bottom-gray">
                        {translations.result_profi.desc_2}
                        <br></br>
                        {translations.result_profi.desc_3}{" "}
                        {roomModel.job_position.company_name}
                      </span>
                      <Button
                        className="button-std button-home"
                        onClick={() => notifyParentToRedirect(appId)}
                      >
                        {translations.result_profi.home}
                      </Button>
                    </>
                  )}
                </>
              ) : appId == "INTERVIEW_AI" ? (
                <>
                  {isRecommending && (
                    <>
                      <CircularProgress className="result-check-img"></CircularProgress>
                      <span className="text-title-bold">
                        {translations.result.title}
                      </span>
                      <span className="text-sub-title">
                        กำลังโหลดคำตอบ และ คำแนะนำเพิ่มเติมให้แก่คุณ
                      </span>
                      <span className="text-sub-title">
                        กรุณา อย่าปิดหน้านี้ โดยเด็ดขาด จนกว่า ผลลัพธ์จะแสดง
                      </span>
                      {isRecommendError && (
                        <>
                          <span className="text-bottom-gray">
                            กรณีที่รอนานเกินไป กรุณากด โหลดซ้ำ เพื่อเช็คอีกครั้ง
                          </span>
                          <Button
                            className="button-std button-home"
                            onClick={() => getReportRecommend(reportId)}
                          >
                            โหลดซ้ำ
                          </Button>
                        </>
                      )}
                    </>
                  )}
                </>
              ) : appId == "PUBLIC_INTERVIEW_AI" ? (
                <>
                  <img
                    src={RESULT_CHECK_IMG}
                    className="result-check-img"
                  ></img>
                  <span className="text-title-bold">
                    {translations.result.title}
                  </span>
                  <span className="text-sub-title">
                    {translations.result.desc_1}
                  </span>
                  <span className="text-position-name">
                    {(roomModel.job_position.name || roomModel.job_position.job_title || "").trim() || "Interview"}
                  </span>
                  {/* <div className="company-control">
                    <img
                      src={roomModel.job_position.company_logo}
                      className="company-logo"
                    ></img>
                    <span className="company-name">
                      {roomModel.job_position.company_name}
                    </span>
                  </div> */}
                  <span className="text-bottom-gray">
                    {" "}
                    {translations.result.desc_2}
                    {translations.result.desc_3}
                    {translations.result.desc_4}
                  </span>
                  <span className="text-bottom-gray">
                    {translations.result.desc_5}
                    <br></br>
                    {translations.result.desc_6}{" "}
                    {roomModel.job_position.company_name}
                  </span>
                </>
              ) : appId === "ENGLISH_INTERVIEW_AI" ? (
                <></>
              ):(
                <>
                  <img
                    src={RESULT_CHECK_IMG}
                    className="result-check-img"
                  ></img>
                  <span className="text-title-bold">
                    {translations.result.title}
                  </span>
                  <span className="text-sub-title">
                    {translations.result.desc_1}
                  </span>
                  <span className="text-position-name">
                    กำลังทำ UI ข้อแนะนำการสัมภาษณ์ของคุณ
                  </span>

                  <span className="text-bottom-gray">
                    {" "}
                    {translations.result.desc_2}
                    {translations.result.desc_3}
                    {translations.result.desc_4}
                  </span>
                  <span className="text-bottom-gray">
                    {translations.result.desc_5}
                    <br></br>
                    {translations.result.desc_6}{" "}
                    {roomModel.job_position.company_name}
                  </span>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {recommend && (
        <div className="recommend-wrapper">
          <div className="result-top-control">
            <img
              src={appId === "PROFI" ? LOGO_PROFI : IMG_JOBHACK}
              onClick={appId === "PROFI" ? () => notifyParentToRedirect("PROFI") : backHome}
              className="result-page-logo-jobhack"
              alt="logo"
            />
            {appId === "PROFI" ? (
              <Button
                className="button-std button-full-report"
                onClick={() => window.open(`${config.partner.profi}?report_id=${reportId}`, "_blank", "noopener,noreferrer")}
              >
                ดูรายงาน
              </Button>
            ) : (
              <Button
                className="button-std button-full-report"
                onClick={showPayment}
              >
                ดู Report แบบเต็ม
              </Button>
            )}
          </div>

          {recommend.hiring_score != null && Number.isFinite(recommend.hiring_score) && (
            <div className="border-std recommend-control recommend-hiring-score-wrap">
              <div className="recommend-hiring-score-inner">
                <span className="recommend-hiring-label">คะแนนประเมินโอกาสได้งาน</span>
                <span className="recommend-hiring-value">{Math.round(recommend.hiring_score)}</span>
                <span className="recommend-hiring-max">/ 100</span>
              </div>
            </div>
          )}

          <div className="border-std recommend-control">
            <span className="text-title-bold">ผลลัพธ์จากการสัมภาษณ์</span>
            <div className="recommend-item-control">
              <span className="recommend-title">ภาพรวมการสัมภาษณ์</span>
              <div
                className="recommend-description"
                dangerouslySetInnerHTML={{
                  __html: recommend.overall_advice_transcript.replace(
                    /\n/g,
                    "<br />"
                  ),
                }}
              ></div>
            </div>
          </div>

          {(recommend.feedback_transcript_logs ?? []).map((item, index) => {
            return (
              <div className="border-std recommend-control" key={item.question_id || index}>
                <div className="recommend-item-control">
                  <span className="recommend-title">คำถามที่ {index + 1}</span>
                  <div className="recommend-value-control">
                    <span className="recommend-question">
                      {item.question.th}
                    </span>
                    <span className="recommend-question">
                      {item.question.en}
                    </span>
                    {item.answer_text && (
                      <div className="recommend-candidate-answer">
                        <span className="recommend-answer-label">คำตอบของคุณ (Transcript)</span>
                        <div className="recommend-answer-text">{item.answer_text}</div>
                      </div>
                    )}
                    <span className="recommend-feedback-label">ข้อเสนอแนะ</span>
                    <div
                      className="recommend-description"
                      dangerouslySetInnerHTML={{
                        __html: item.feedback.replace(/\n/g, "<br />"),
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            );
          })}

          <div className="border-std recommend-control">
            <div className="recommend-full-report-control">
              <img src={IMG_COVER} className="report-img-cover"></img>
              <div className="recommend-list-full">
                <div className="report-list-right">
                  <span className="report-full-title">
                    มีอะไรบ้างใน Fully Report
                  </span>
                  <ul>
                    <li className="report-full-desc">
                      สรุปบทสัมภาษณ์ (Transcript Summary): <br></br>
                      <span>
                        สรุปเนื้อหาการสัมภาษณ์ทั้งหมดอย่างชัดเจนและกระชับ
                      </span>
                    </li>
                    <li className="report-full-desc">
                      ประเมินทักษะ (Skill Evaluation):<br></br>{" "}
                      <span>
                        วิเคราะห์ทักษะการสื่อสาร การแก้ปัญหา ภาวะผู้นำ
                        และทักษะเฉพาะงาน
                      </span>
                    </li>
                    <li className="report-full-desc">
                      วิเคราะห์ Soft Skills: <br></br>
                      <span>
                        ประเมินความฉลาดทางอารมณ์ ความมั่นใจ น้ำเสียง
                        และระดับการมีส่วนร่วม
                      </span>
                    </li>
                    <li className="report-full-desc">
                      คะแนนรวม (Scorecard): <br></br>
                      <span>คะแนนจาก AI ที่ประเมินคุณในแต่ละด้านสำคัญ</span>
                    </li>
                    <li className="report-full-desc">
                      คำแนะนำเฉพาะบุคคล: <br></br>
                      <span>
                        ข้อเสนอแนะเพื่อพัฒนาทักษะและเตรียมตัวให้ดียิ่งขึ้น
                      </span>
                    </li>
                    <li className="report-full-desc">
                      ความเหมาะสมกับงาน (Role Fit Insights): <br></br>
                      <span>
                        วิเคราะห์ว่าคุณเหมาะกับตำแหน่งใด และมีจุดแข็งตรงไหน
                      </span>
                    </li>
                    <li className="report-full-desc">
                      รายงาน PDF พร้อมดาวน์โหลด: <br></br>
                      <span>
                        รูปแบบสวยงาม เป็นมืออาชีพ แชร์หรือเก็บไว้ใช้ได้
                      </span>
                    </li>
                  </ul>
                </div>
                <div className="report-list-right">
                  <span className="report-full-title">
                    ทำไมคุณถึงต้องยอมจ่าย
                  </span>
                  <ul>
                    <li className="report-full-desc">
                      🧠 การวิเคราะห์โดย AI ระดับผู้เชี่ยวชาญ: <br></br>
                      <span>
                        ใช้ AI ที่ได้รับการฝึกจากบทสัมภาษณ์นับพันรายการ
                        เพื่อให้คุณได้ข้อมูลเหมือนมี HR มืออาชีพวิเคราะห์
                      </span>
                    </li>
                    <li className="report-full-desc">
                      🎯 คำแนะนำเฉพาะคุณเท่านั้น: <br></br>
                      <span>
                        ไม่ใช่คำแนะนำทั่วไป
                        แต่คือข้อมูลที่ออกแบบมาเพื่อคุณโดยเฉพาะ
                      </span>
                    </li>
                    <li className="report-full-desc">
                      📊 ข้อมูลเชิงลึกที่นำไปใช้ได้จริง: <br></br>
                      <span>รู้จุดแข็ง-จุดอ่อน และสามารถพัฒนาต่อได้ทันที</span>
                    </li>
                    <li className="report-full-desc">
                      🔒 ปลอดภัยและเป็นส่วนตัว: <br></br>
                      <span>
                        ข้อมูลของคุณจะถูกเก็บรักษาไว้อย่างปลอดภัย
                        เข้าถึงได้เฉพาะคุณเท่านั้น
                      </span>
                    </li>
                    <li className="report-full-desc">
                      📝 เหมาะกับทุกคนที่ต้องการเติบโตในสายอาชีพ: <br></br>
                      <span>
                        ทั้งผู้หางาน นักศึกษา หรือคนที่กำลังจะเลื่อนตำแหน่ง
                      </span>
                    </li>
                    <li className="report-full-desc">
                      💼 สร้างความได้เปรียบในตลาดงาน: <br></br>
                      <span>
                        เตรียมพร้อมเหนือคู่แข่งด้วยการเตรียมตัวที่ดีกว่าด้วย AI
                      </span>
                    </li>
                  </ul>
                </div>
              </div>

              <Button className="button-std button-home" onClick={showPayment}>
                กดเพื่อดู Fully Report
              </Button>
            </div>
          </div>

          <div className="recommend-control">
            <Button
              className="button-std button-home"
              onClick={appId === "PROFI" ? () => notifyParentToRedirect("PROFI") : backHome}
            >
              {appId === "PROFI" ? (translations.result_profi?.home ?? "กลับหน้าหลัก") : "กลับหน้าหลัก"}
            </Button>
          </div>
        </div>
      )}
      {isError && (
        <ErrorModal
          isOpen={isError}
          onClose={() => setIsError(false)}
          message="ไม่มีข้อมูลการสัมภาษณ์"
        ></ErrorModal>
      )}
      {isShowPayment && (
        <PaymentModal
          isOpen={isShowPayment}
          onClose={showPayment}
        ></PaymentModal>
      )}
    </>
  );
};

export default ResultPage;
